import { createContext, useContext, useLayoutEffect, useRef, type ReactNode } from 'react';
import type { EntityPrivacy, PresenceSettings } from '@cdf/api-client-react';
import type { QueryClient, QueryKey } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';

export const privacyModeLabels = {
  always_private: 'Always private', always_open: 'Always open', user_defined: 'User defined'
} as const;

export function personalPrivacyEditable(settings: PresenceSettings | undefined, settled: boolean): boolean {
  return settled && settings?.privacy?.mode === 'user_defined' && settings.personalSettingsEditable === true;
}

export function isDisclosureQuery(key: QueryKey): boolean {
  const first = String(key[0] ?? '');
  return /^\/api\/(presence|colleagues|workgroups)(\/|$)/.test(first) ||
    /^\/api\/floors\/[^/]+\/bookings$/.test(first) ||
    ['floor-bookings-table', 'colleague-desks', 'booking-availability', 'availability'].includes(first) ||
    (first === 'graph' && key.includes('teams-presence'));
}

/** Reset, rather than merely mark stale: pending responses and old facts must be discarded. */
export async function resetPrivacyDisclosure(client: QueryClient): Promise<void> {
  const filter = { predicate: (query: { queryKey: QueryKey }) => isDisclosureQuery(query.queryKey) };
  await client.cancelQueries(filter);
  await client.resetQueries(filter);
}

type PrivacyContext = { privacy: EntityPrivacy | null; ready: boolean; key: string };
const Context = createContext<PrivacyContext | null>(null);
export function useEntityPrivacy() { return useContext(Context); }

/** Existing branding/settings observer supplies a single current policy for every entity surface. */
export function EntityPrivacyProvider({ privacy, ready, authority, children }: {
  privacy: EntityPrivacy | undefined; ready: boolean; authority: string; children: ReactNode;
}) {
  const client = useQueryClient();
  const settled = ready && Boolean(privacy);
  const key = JSON.stringify([authority, settled ? privacy?.revision : null, settled ? privacy?.mode : null]);
  const previous = useRef(key);
  useLayoutEffect(() => {
    if (previous.current === key) return;
    previous.current = key;
    void resetPrivacyDisclosure(client);
  }, [client, key]);
  return <Context.Provider value={{ privacy: settled ? privacy! : null, ready: settled, key }}>
    {children}
  </Context.Provider>;
}
